import "@merchant-trends/ds-tokens/css";
import "@merchant-trends/ds-tokens/css/component-states";
import "@nexus/nexus-shell/styles.css";
import { createQueryClient } from "@nexus/nexus-shell";
import { QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter, RouterProvider } from "react-router";
import { bootstrapApplication } from "./bootstrap.js";
import { renderBootstrapError } from "./bootstrap-error.js";
import { requireMountElement } from "./mount-point.js";
import { createPortalRoutes } from "./routes.js";
import { loadRuntimeConfig } from "./runtime-config.js";

/**
 * Browser entry point. Wiring only, per `docs/TESTING-STANDARDS.md` "Seams": every branch it used
 * to hold now lives behind a tested seam - `runtime-config.ts` loads and validates,
 * `bootstrap.ts` decides, `bootstrap-error.ts` renders the failure - so there is nothing here for
 * a test to reach and nothing here a test cannot reach through those.
 *
 * The first two stylesheet imports are the token layer's published side-effect entry points; the
 * third is `@nexus/nexus-shell`'s Tailwind output for the design system's composite components,
 * without which the Nexus frame lays out unstyled (NEX2-232). They are
 * imported here rather than inside a component because `@merchant-trends/ds-ui` deliberately does
 * not inject them: its components read `var(--color-*)` and `var(--space-*)` at runtime and render
 * unstyled if nothing has loaded the custom properties. `./css/chart-motion` is left out until a
 * chart is actually mounted - it is a separate entry point precisely so an application that draws
 * nothing does not ship it.
 *
 * `FE-06`/`FE-07`: the application does not mount until the runtime configuration has loaded and
 * validated, and never mounts at all if it has not. The router is created inside `mount`, after
 * that, because the Nexus frame reports the API origin and links to the apps the document names
 * (NEX2-232) - there is nothing to build the routes with before then.
 *
 * `createQueryClient()` is called once, inside the success path, rather than on every render:
 * this file's whole body runs exactly once regardless of how React re-renders the tree beneath it,
 * so there is no need for `useState`'s once-per-mount guarantee the way there would be inside a
 * component. The factory is `@nexus/nexus-shell`'s, shared with Checkout (NEX2-232). FE-09's reset
 * story - replacing the client when an organization or site changes - arrives with the site
 * switcher, the next NEX2-232 pull request.
 */
const container = requireMountElement(document, "root");

void bootstrapApplication({
  loadConfig: loadRuntimeConfig,
  mount: (config) => {
    const queryClient = createQueryClient();
    const router = createBrowserRouter(createPortalRoutes(config));
    createRoot(container).render(
      <StrictMode>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
        </QueryClientProvider>
      </StrictMode>,
    );
  },
  renderError: (message) => {
    renderBootstrapError(container, message);
  },
});
